import React from 'react';
import { Link } from 'react-router';
import { EnvelopeIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '../components/layouts';
import { Seo } from '../seo/Seo';
import {
  DeckHero,
  DeckSection,
  DeckSectionHeader,
  DeckTwoColumn,
  DeckFinalCta,
  useDeckAnimateOnScroll,
} from '../components/landing/deck';

const Contact: React.FC = () => {
  const { t } = useTranslation(['contact', 'common']);
  useDeckAnimateOnScroll();

  const renderColumn = (scope: 'sales' | 'general', topicKeys: readonly string[]) => (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-sky-400 mb-3">
        {t(`contact:${scope}.eyebrow`)}
      </p>
      <h2 className="text-2xl font-light text-white mb-4">{t(`contact:${scope}.title`)}</h2>
      <p className="text-white/60 mb-8 leading-relaxed">{t(`contact:${scope}.description`)}</p>
      <a
        href={`mailto:${t(`contact:${scope}.email`)}`}
        className="inline-flex items-center gap-3 text-base text-sky-400 hover:text-sky-300 transition-colors mb-10"
      >
        <EnvelopeIcon className="w-5 h-5" />
        {t(`contact:${scope}.email`)}
      </a>
      <div className="border-t border-white/10 pt-6">
        <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-4">
          {t(`contact:${scope}.topics.title`)}
        </p>
        <ul className="space-y-3 text-sm text-white/70">
          {topicKeys.map((k) => (
            <li key={k} className="flex items-start gap-3">
              <span className="text-sky-400 mt-1">—</span>
              <span>{t(`contact:${scope}.topics.${k}`)}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  return (
    <PublicLayout transparentHeader>
      <Seo route="/contact" />

      <DeckHero title={t('contact:hero.title')} subtitle={t('contact:hero.subtitle')} />

      <DeckSection>
        <DeckSectionHeader badge={t('contact:sales.eyebrow')} title={t('contact:sales.title')} />
        <DeckTwoColumn
          left={renderColumn('sales', ['demo', 'pricing', 'enterprise', 'partnerships'])}
          right={renderColumn('general', ['press', 'feedback', 'other'])}
        />
      </DeckSection>

      <DeckSection size="compact">
        <div className="max-w-3xl">
          <DeckSectionHeader title={t('contact:response.title')} />
          <p className="text-lg text-white/60 leading-relaxed animate-on-scroll">
            {t('contact:response.description')}
          </p>
        </div>
      </DeckSection>

      <DeckSection size="compact">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 animate-on-scroll">
          <div>
            <h2 className="text-2xl font-light text-white mb-2">{t('contact:support.title')}</h2>
            <p className="text-white/60">{t('contact:support.description')}</p>
          </div>
          <Link
            to="/support"
            className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 text-base font-medium transition-colors group flex-shrink-0"
          >
            {t('contact:support.cta')}
            <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </DeckSection>

      <DeckFinalCta
        title={t('contact:hero.title')}
        subtitle={t('contact:hero.subtitle')}
        email="ventas@callcov.com"
      />
    </PublicLayout>
  );
};

export default Contact;
